import { z } from 'zod';
// Self-hosted on ai-01 through vLLM.
export const localModelIds = ['gemma4-26b'];
// Hosted providers, called with the API's own credentials.
export const hostedModelIds = [
    'gpt-4.1-nano',
    'gemini-3.1-flash-lite',
];
export const modelIds = [...localModelIds, ...hostedModelIds];
// Decision models answer typed questions on /v1/decide; they never generate text.
export const decisionModelIds = ['jev-1.13'];
// Every model a key may be granted, as listed by /v1/models.
export const catalogModelIds = [...modelIds, ...decisionModelIds];
// Requests may select 'auto'; responses always identify the resolved model.
export const generationModels = ['auto', ...modelIds];
export const routingReasons = [
    'explicit',
    'local_ready',
    'scheduled_hosted',
    'local_unavailable',
];
// Why generation ended, as a fixed vocabulary: provider text never passes through.
export const finishReasons = ['stop', 'max_output_tokens', 'content_filter', 'other'];
export const modelIdSchema = z.enum(modelIds);
export function isLocalModelId(model) {
    return localModelIds.includes(model);
}
// Check for content without trimming teacher-authored prompts or HTML.
const nonEmptyTextSchema = z.string().refine(value => value.trim().length > 0, 'Text must not be empty.');
const tokenCountSchema = z.number().int().nonnegative();
export const textMessageSchema = z.strictObject({
    role: z.enum(['system', 'developer', 'user', 'assistant']),
    content: nonEmptyTextSchema,
});
export const jsonFormatSchema = z.strictObject({
    type: z.literal('json_schema'),
    name: z.string().regex(/^[\w-]{1,64}$/),
    strict: z.boolean().optional(),
    // JSON Schema has its own vocabulary; AJV validates it on the API.
    schema: z.record(z.string(), z.unknown()),
});
/** Requests sent to the AI API, including explicit models and server-side 'auto' routing. */
export const generateRequestSchema = z.strictObject({
    model: z.enum(generationModels),
    input: z.union([
        nonEmptyTextSchema,
        z.array(textMessageSchema).min(1).max(100),
    ]),
    format: jsonFormatSchema.optional(),
    temperature: z.number().min(0).max(2).optional(),
    top_p: z.number().min(0).max(1).optional(),
    max_output_tokens: z.number().int().positive().optional(),
});
export const usageSchema = z.strictObject({
    input_tokens: tokenCountSchema,
    output_tokens: tokenCountSchema,
    total_tokens: tokenCountSchema,
});
export const localRuntimeSchema = z.strictObject({
    revision: nonEmptyTextSchema,
    fingerprint: nonEmptyTextSchema,
    image: nonEmptyTextSchema,
});
export const generateResultSchema = z.strictObject({
    requestId: nonEmptyTextSchema,
    model: modelIdSchema,
    actualModel: nonEmptyTextSchema,
    text: z.string(),
    data: z.unknown().optional(),
    usage: usageSchema.nullable(),
    durationMs: z.number().nonnegative(),
    runtime: localRuntimeSchema.optional(),
    finishReason: z.enum(finishReasons).optional(),
    // Older explicit-model deployments omit routing metadata.
    requestedModel: z.enum(generationModels).optional(),
    routingReason: z.enum(routingReasons).optional(),
});
export const modelInfoSchema = z.strictObject({
    id: z.enum(catalogModelIds),
    provider: z.enum(['vllm', 'openai', 'gemini', 'typesafe']),
    actualModel: nonEmptyTextSchema,
    available: z.boolean(),
    reason: z.string().optional(),
    revision: z.string().optional(),
});
export const modelsResponseSchema = z.strictObject({
    requestId: nonEmptyTextSchema.optional(),
    models: z.array(modelInfoSchema),
});
